import { get, concat, remove } from 'lodash';
import { ref } from 'vue';
import { getReportData, exportReport } from '@/infrastructure/api-functions';
import { i18n } from '@/infrastructure/locales'
import { formatDate } from '@eureka/utils/date-format';
import html2canvas from 'html2canvas'
import type {
    IDocCompareData,
    IBaseSolution,
    IExportReportData,
    IReportPatent,
} from '@/types';
import { useGlobalState } from '@/composables/use-global-store';
import { Toast } from 'patsnap-biz';
import { createIframeDownload } from '@eureka/services/src/flow';

const MAX_LIMIT = 10;
export class ReportService {
    reportList: IBaseSolution[] = [];

    get editContent() {
        const { editContent } = useGlobalState();
        return editContent
    }

    get inputLang() {
        const { inputLang } = useGlobalState();
        return inputLang.value
    }

    get reportLength() {
        return this.reportList.length
    }

    get allGptFinish() {
        return this.finishCount === this.reportLength
    }

    add(solution: IBaseSolution) {
        if (this.reportLength >= MAX_LIMIT) {
            Toast({
                message: i18n.global.t('addReportLimit'),
                type: 'warning',
            })
            throw new Error('Limit Max')
        }
        this.reportList = concat(this.reportList, { ...solution, addedReport: true })
    }

    removeChosen(solution: IBaseSolution) {
        remove(this.reportList, (i) => i.patent_id === solution.patent_id)
    }

    /**
     * @description: 报告名称
     */
    title = '';
    /**
     * @description: 是否允许下载
     */
    downloadPermission = false;
    exportLoading = false;

    /**
     * @description: GPT总结是否完成，如果完成才允许下载
     */
    finishCount = 0;
    /**
     * @description: 是否新颖性或创造性
     */
    isNovelty = true;
    /**
     * @description: 技术方案分析结果
     */
    analysisResult = '';
    /**
     * @description: 报告日期
     */
    date = formatDate({ date: new Date() });
    /**
     * @description: 专利列表
     */
    patentList: IReportPatent[] = [];
    /**
     * @description: 文档对比列表
     */
    docCompareList: IDocCompareData[] = [];
    /**
     * @description: 结果列表
     */
    cloudImg = '';
    heatImg = '';
    cloudTopRank: string[] = [];
    heatTopRank: string[] = [];
    isChartLoaded = false;
    isPPT = false
    /**
     * @description: 风险点列表
     */
    get riskPointList(): string[] {
        const pointList: string[] = [];
        if (!this.editContent) {
            return pointList;
        }
        const { means, efficacy } = this.editContent;
        if (means) {
            pointList.push(`${i18n.global.t('means')}️：${means}`);
        }
        if (efficacy) {
            pointList.push(`${i18n.global.t('efficacy')}️：${efficacy}`);
        }
        return pointList;
    }

    fetchReportData() {
        this.finishCount = 0;
        this.docCompareList = [];
        if (!this.reportList.length) return
        const pair_patents = this.reportList.map((item) => {
            return {
                patent_id: item.patent_id,
                similarity: item.similarity,
            };
        })
        return getReportData({
            pair_patents,
            lang: this.inputLang,
            source_means: this.editContent?.means,
            source_efficacy: this.editContent?.efficacy,
        }).then(data => {
            this.title = i18n.global.t('reportTitle');
            this.isNovelty = get(data, 'has_novelty', false);
            this.patentList = get(data, 'pair_patents', []);
            this.analysisResult = get(data, 'analysis_result', '');
            const noDataText = i18n.global.t('noDataPlaceholder')
            this.docCompareList = this.patentList.map((item) => {
                const { title, pn, org, similarity, org_logo: orgLogo, patent_id } = item;
                return {
                    info: { title, pn, org, similarity, orgLogo, patent_id },
                    list: [
                        { title: i18n.global.t('technicalProblem'), description: item.tech_pro || noDataText, images: [] },
                        { title: i18n.global.t('means'), description: item.tech_mean || noDataText, images: [] },
                        { title: i18n.global.t('efficacy'), description: item.tech_efficacy || noDataText, images: item.figures || [] }
                    ],
                    featureList: [
                        { curr: this.editContent?.means || noDataText, patent: item.means || i18n.global.t('noMeans') },
                        { curr: this.editContent?.efficacy || noDataText, patent: item.efficacy || i18n.global.t('noEfficacy') }
                    ],
                }
            });
        })
    }

    downloadReport(isPPT = false) {
        this.isPPT = isPPT
        this.exportLoading = true;
        if (isPPT) {
            const scopeCard = document.getElementById('risk-scope-card') as HTMLElement
            setTimeout(() => {
                html2canvas(scopeCard, {
                    useCORS: true,
                    scale: 1,
                    imageTimeout: 0,
                    onclone: (document) => {
                        // 解决截图之后的国旗图片和文字不居中的问题
                        const clonedElement = document.querySelectorAll('.country-flag-authority') as NodeListOf<HTMLElement>
                        clonedElement?.forEach((element) => {
                            element.style.marginTop = '12px';
                        });
                    },
                }).then((canvas) => {
                    const dataURL = canvas.toDataURL('image/png')
                    this.downloadFetch(isPPT, dataURL)
                }).catch((error) => {
                    console.log(error);
                })
            }, 100)
        } else {
            this.downloadFetch(isPPT)
        }
    }

    downloadFetch(isPPT = false, country_img = '') {
        const exportData: IExportReportData = {
            lang: this.inputLang,
            pair_patents: this.patentList,
            country_img,
            is_ppt: isPPT,
            has_novelty: this.isNovelty,
            source_means: this.editContent?.means,
            source_efficacy: this.editContent?.efficacy,
            result: this.analysisResult,
            company: this.patentList[0]?.org,
            company_cloud: this.cloudTopRank[0] || '',
            company_phrase: this.heatTopRank || [],
            analysis_cloud_img: this.cloudImg,
            analysis_efficacy_img: this.heatImg,
        };
        exportReport(exportData).then((url) => {
            createIframeDownload(url);
        }).finally(() => {
            this.exportLoading = false;
        });
    }

    clear() {
        this.isChartLoaded = false;
    }
}

export const reportService = ref<ReportService>(new ReportService());
